import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormHelperText,
  Grid,
  TextField
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { changePassword } from 'src/actions/accountActions';

const useStyles = makeStyles(() => ({
  root: {}
}));

function Security({ className, ...rest }) {
  const { user } = useSelector(state => state.account);
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  return (
    <Formik
      initialValues={{
        password: '',
        passwordConfirm: '',
        oldPassword: ''
      }}
      validationSchema={Yup.object().shape({
        password: Yup.string()
          .min(8, 'Måste vara minst 8 tecken')
          .max(255)
          .required('Vänligen ange ett lösenord'),
        oldPassword: Yup.string()
          .max(255)
          .required('Vänligen ange ett lösenord'),
        passwordConfirm: Yup.string()
          .oneOf([Yup.ref('password'), null], 'De angivna lösenorden måste vara samma')
          .required('Vänligen ange ett lösenord')
      })}
      onSubmit={async (values, {
        resetForm,
        setErrors,
        setStatus,
        setSubmitting
      }) => {
        try {
          await dispatch(changePassword(values));
          resetForm();
          setStatus({ success: true });
          enqueueSnackbar('Lösenordet är uppdaterat', {
            variant: 'success'
          });

          if(user.changeOnNextLogin === true){
            window.location = '/';
          }
        } catch (error) {
          setStatus({ success: false });

          if (error.response && error.response.data.Message) {
            setErrors({ submit: error.response.data.Message });
          }
          else {
            setErrors({ submit: error.message });
          }
        } finally {
          setSubmitting(false);
        }

      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values
      }) => (
        <form onSubmit={handleSubmit}>
          <Card
            className={clsx(classes.root, className)}
            {...rest}
            sx={{margin: user?.usertype === 3 ? 2 : ""}}
          >
            <CardHeader title="Ändra lösenord" />
            <Divider />
            <CardContent>
              <Grid
                container
                spacing={3}
              >
                <Grid
                  item
                  md={4}
                  sm={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.oldPassword && errors.oldPassword)}
                    fullWidth
                    helperText={touched.oldPassword && errors.oldPassword}
                    label="Gammalt lösenord"
                    name="oldPassword"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="oldPassword"
                    value={values.oldPassword}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  md={4}
                  sm={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.password && errors.password)}
                    fullWidth
                    helperText={touched.password && errors.password}
                    label="Lösenord"
                    name="password"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="password"
                    value={values.password}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  md={4}
                  sm={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.passwordConfirm && errors.passwordConfirm)}
                    fullWidth
                    helperText={touched.passwordConfirm && errors.passwordConfirm}
                    label="Bekräfta lösenord"
                    name="passwordConfirm"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="password"
                    value={values.passwordConfirm}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
              {errors.submit && (
                <Box mt={3}>
                  <FormHelperText error>
                    {errors.submit}
                  </FormHelperText>
                </Box>
              )}
            </CardContent>
            <Divider />
            <Box
              p={2}
              display="flex"
              justifyContent="flex-end"
            >
              <Button
                color="secondary"
                disabled={isSubmitting}
                type="submit"
                variant="contained"
              >
                Ändra lösenord
              </Button>
            </Box>
          </Card>
        </form>
      )}
    </Formik>
  );
}

Security.propTypes = {
  className: PropTypes.string
};

export default Security;
